import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag } from "lucide-react";
import BanyanMark from "@/components/BanyanMark";
import { scrollToId } from "@/lib/scroll";
import { trackClick } from "@/lib/api";
import { AMAZON_STORE } from "@/data/books";

const LINKS = [
  { label: "The Books", id: "library", testId: "nav-link-books" },
  { label: "Panchatantra", route: "/panchatantra", testId: "nav-link-panchatantra" },
  { label: "Manifesto", id: "manifesto", testId: "nav-link-manifesto" },
  { label: "Activity Pack", id: "activity-pack", testId: "nav-link-activity-pack" },
  { label: "Contact", route: "/contact", testId: "nav-link-contact" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (link) => {
    setOpen(false);
    if (link.route) {
      navigate(link.route);
      window.__lenis?.scrollTo(0, { immediate: true });
      window.scrollTo(0, 0);
      return;
    }
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(link.id), 600);
    } else {
      scrollToId(link.id);
    }
  };

  const goHome = () => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
    } else {
      window.__lenis?.scrollTo(0);
    }
  };

  const solid = scrolled || location.pathname !== "/";

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-50 border-b transition-[background-color,border-color] duration-500 ${
        solid
          ? "border-gold/20 bg-emerald-ink/85 backdrop-blur-xl"
          : "border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
        <button
          onClick={goHome}
          data-testid="nav-logo"
          aria-label="Banyan Tree Press — back to top"
          className="flex items-center gap-3 text-left"
        >
          <img
            src="/logo-192.webp"
            alt="Banyan Books logo"
            className="h-10 w-10 rounded-full ring-1 ring-gold/50"
          />
          <span className="leading-tight">
            <span className="block font-serif text-lg font-semibold tracking-wide text-cream">
              Banyan Tree Press
            </span>
            <span className="block font-mono text-[10px] uppercase tracking-[0.28em] text-gold/80">
              Ancient Wisdom · Modern Magic
            </span>
          </span>
        </button>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <button
              key={l.testId}
              data-testid={l.testId}
              onClick={() => go(l)}
              className="group relative font-mono text-xs uppercase tracking-[0.2em] text-cream/75 transition-colors hover:text-gold"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
          <a
            href={AMAZON_STORE}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-amazon-btn"
            onClick={() => trackClick("nav-shop-amazon", AMAZON_STORE, "nav")}
            className="flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-emerald-ink transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-glow"
          >
            <ShoppingBag className="h-3.5 w-3.5" />
            Shop on Amazon
          </a>
        </nav>

        <button
          className="text-cream lg:hidden"
          data-testid="nav-mobile-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-gold/15 bg-emerald-ink/95 backdrop-blur-xl lg:hidden"
            aria-label="Mobile"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {LINKS.map((l) => (
                <button
                  key={l.testId}
                  data-testid={`${l.testId}-mobile`}
                  onClick={() => go(l)}
                  className="py-3 text-left font-serif text-2xl text-cream transition-colors hover:text-gold"
                >
                  {l.label}
                </button>
              ))}
              <a
                href={AMAZON_STORE}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-amazon-btn-mobile"
                onClick={() => trackClick("nav-shop-amazon-mobile", AMAZON_STORE, "nav")}
                className="mt-4 flex w-fit items-center gap-2 rounded-full bg-gold px-6 py-3 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-emerald-ink"
              >
                <ShoppingBag className="h-3.5 w-3.5" />
                Shop on Amazon
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
